import type {
  MotionTokens,
  RadiusTokens,
  SpacingTokens,
  Theme,
  TypographyTokens,
} from '../../theme/contract';

/**
 * Warm Neutral + Teal — the parent/relative ("family" persona) theme. Token values are extracted from
 * the signed-off mockup deck `docs/parent-preview/_kit.css`: a warm-neutral canvas, a single teal
 * brand/accent, amber "needs you / waiting", terracotta danger, Inter font, soft neutral shadows, and
 * slightly generous radii. The tone is calm, capable, grown-up — never a dense enterprise dashboard.
 *
 * This is the SOLE family theme (cozy-pastel is now kid-only, command-center stays admin-only).
 * Coins stay GOLD and effort-stars VIOLET via the dedicated currency components (CoinChip / StarChip),
 * NOT theme tokens — so they read identically across every theme.
 *
 * Non-color tokens are shared between light and dark; only the color set differs.
 */

const radius: RadiusTokens = {
  sm: '10px',
  md: '14px',
  lg: '18px',
  card: '18px',
  pill: '999px',
};

const spacing: SpacingTokens = {
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '32px',
};

const typography: TypographyTokens = {
  fontSans: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontDisplay: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontMono: "'Inter', ui-monospace, 'SFMono-Regular', 'Menlo', monospace",
  sizeXs: '11px',
  sizeSm: '12.5px',
  sizeBase: '14px',
  sizeMd: '15px',
  sizeLg: '18px',
  sizeXl: '21px',
  sizeXxl: '30px',
  weightNormal: '400',
  weightMedium: '500',
  weightSemibold: '600',
  weightBold: '700',
  trackingTight: '-0.01em',
  leadingTight: '1.15',
  leadingNormal: '1.5',
};

const motion: MotionTokens = {
  durationFast: '110ms',
  durationNormal: '160ms',
  durationSlow: '280ms',
  easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeBounce: 'cubic-bezier(0.34, 1.4, 0.64, 1)',
};

export const warmNeutralTeal: Theme = {
  id: 'warm-neutral-teal',
  light: {
    color: {
      bg: '#FAFAF8',
      surface: '#FFFFFF',
      surfaceMuted: '#F2F1EC',
      primary: '#2F8F77',
      primaryFg: '#FFFFFF',
      // Secondary = the kit's calm "info" blue (used for the learning/info pill) — a sober complement.
      secondary: '#3F6E8C',
      secondaryFg: '#FFFFFF',
      // Accent = the soft teal wash + deep-teal ink (matches the kit's --accent-soft / --accent-deep).
      accent: '#E3F0EB',
      accentFg: '#246F5C',
      text: '#1C1B1A',
      textMuted: '#6B6862',
      border: '#E7E5DE',
      input: '#D8D5CC',
      // "Done / approved" reads teal in the deck — success is the deep teal ink on its soft teal tint
      // (AA on both the tint and white surface), `successFg` is the pale teal pill background.
      success: '#246F5C',
      successFg: '#E3F0EB',
      // "Needs you / waiting" = amber (AA-dark ink on the soft amber tint).
      warning: '#A66A1E',
      warningFg: '#F6EAD6',
      // Destructive / missed / rejected = terracotta (white text on the danger fill).
      danger: '#B4554C',
      dangerFg: '#FFFFFF',
      // Effort-stars (✨) = violet — the third currency, unmistakable vs coins (gold) / cash (green).
      star: '#7257C7',
      starFg: '#ECE7FA',
      ring: '#2F8F77',
    },
    // Categorical series in the family voice: teal → info-blue → amber → violet → terracotta → warm
    // neutral. `grid`/`axis` echo the border/muted-text tokens so chart chrome stays quiet.
    chart: {
      '1': '#2F8F77',
      '2': '#3F6E8C',
      '3': '#A66A1E',
      '4': '#7257C7',
      '5': '#B4554C',
      '6': '#8A8472',
      grid: '#E7E5DE',
      axis: '#6B6862',
      cursor: '#E3F0EB',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 1px 2px rgba(28,27,26,.05), 0 1px 1px rgba(28,27,26,.04)',
      md: '0 6px 18px rgba(28,27,26,.07), 0 2px 5px rgba(28,27,26,.05)',
      lg: '0 18px 44px rgba(28,27,26,.10), 0 5px 12px rgba(28,27,26,.06)',
      card: '0 1px 2px rgba(28,27,26,.05), 0 0 0 1px rgba(28,27,26,.03)',
      nav: '0 -4px 16px rgba(28,27,26,.06)',
    },
    motion,
  },
  dark: {
    color: {
      bg: '#16161A',
      surface: '#1F1F24',
      surfaceMuted: '#26262C',
      primary: '#5FB89F',
      primaryFg: '#10221C',
      secondary: '#8FB6CE',
      secondaryFg: '#1E2A33',
      accent: '#21332D',
      accentFg: '#7FCBB4',
      text: '#ECEAE6',
      textMuted: '#9A968E',
      border: '#33333A',
      input: '#3F3F47',
      success: '#7FCBB4',
      successFg: '#21332D',
      warning: '#E0A85C',
      warningFg: '#322A1C',
      danger: '#E0897F',
      dangerFg: '#321E1A',
      star: '#A892F0',
      starFg: '#241F33',
      ring: '#5FB89F',
    },
    chart: {
      '1': '#5FB89F',
      '2': '#8FB6CE',
      '3': '#E0A85C',
      '4': '#A892F0',
      '5': '#E0897F',
      '6': '#B6AE97',
      grid: '#33333A',
      axis: '#9A968E',
      cursor: '#21332D',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 1px 2px rgba(0,0,0,.35)',
      md: '0 6px 18px rgba(0,0,0,.40), 0 2px 6px rgba(0,0,0,.30)',
      lg: '0 18px 44px rgba(0,0,0,.50), 0 6px 14px rgba(0,0,0,.34)',
      card: '0 1px 2px rgba(0,0,0,.40), 0 0 0 1px rgba(255,255,255,.04)',
      nav: '0 -4px 16px rgba(0,0,0,.45)',
    },
    motion,
  },
};
